
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Sorting} from './Sorting';
import {FieldModelSorting} from './Sorting';

/**
 * OrderStateSorting struct
 */
class OrderStateSorting {
  /**
   * Initialize struct
   * @param {Sorting=} orderId
   * @param {Sorting=} marginRate
   * @param {Sorting=} profit
   * @param {Sorting=} currentPrice
   * @param {Sorting=} dayProfit
   * @constructor
   */
  constructor (orderId = null, marginRate = null, profit = null, currentPrice = null, dayProfit = null) {
    this.OrderId = orderId
    this.MarginRate = marginRate
    this.Profit = profit
    this.CurrentPrice = currentPrice
    this.DayProfit = dayProfit
  }

  /**
   * Copy struct (shallow copy)
   * @this {!OrderStateSorting}
   * @param {!OrderStateSorting} other Other struct
   * @returns {!OrderStateSorting} This struct
   */
  copy (other) {
    if (other.OrderId != null) {
      this.OrderId = Sorting.fromObject(other.OrderId)
    } else {
      this.OrderId = null
    }
    if (other.MarginRate != null) {
      this.MarginRate = Sorting.fromObject(other.MarginRate)
    } else {
      this.MarginRate = null
    }
    if (other.Profit != null) {
      this.Profit = Sorting.fromObject(other.Profit)
    } else {
      this.Profit = null
    }
    if (other.CurrentPrice != null) {
      this.CurrentPrice = Sorting.fromObject(other.CurrentPrice)
    } else {
      this.CurrentPrice = null
    }
    if (other.DayProfit != null) {
      this.DayProfit = Sorting.fromObject(other.DayProfit)
    } else {
      this.DayProfit = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!OrderStateSorting}
   * @returns {!OrderStateSorting} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new OrderStateSortingModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new OrderStateSortingModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!OrderStateSorting}
   * @param {!OrderStateSorting} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof OrderStateSorting)) {
      throw new TypeError('Instance of OrderStateSorting is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!OrderStateSorting}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      OrderId: ((this.OrderId != null) ? this.OrderId : null),
      MarginRate: ((this.MarginRate != null) ? this.MarginRate : null),
      Profit: ((this.Profit != null) ? this.Profit : null),
      CurrentPrice: ((this.CurrentPrice != null) ? this.CurrentPrice : null),
      DayProfit: ((this.DayProfit != null) ? this.DayProfit : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return OrderStateSorting.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!OrderStateSorting} other Object value
   * @returns {!OrderStateSorting} Created struct
   */
  static fromObject (other) {
    return new OrderStateSorting().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!OrderStateSorting}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return OrderStateSorting.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!OrderStateSorting}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 162
  }
}

export { OrderStateSorting };

/**
 * Fast Binary Encoding OrderStateSorting field model
 */
class FieldModelOrderStateSorting extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.OrderId = new fbe.FieldModelOptional(new FieldModelSorting(buffer, 4 + 4), buffer, 4 + 4)
    this.MarginRate = new fbe.FieldModelOptional(new FieldModelSorting(buffer, this.OrderId.fbeOffset + this.OrderId.fbeSize), buffer, this.OrderId.fbeOffset + this.OrderId.fbeSize)
    this.Profit = new fbe.FieldModelOptional(new FieldModelSorting(buffer, this.MarginRate.fbeOffset + this.MarginRate.fbeSize), buffer, this.MarginRate.fbeOffset + this.MarginRate.fbeSize)
    this.CurrentPrice = new fbe.FieldModelOptional(new FieldModelSorting(buffer, this.Profit.fbeOffset + this.Profit.fbeSize), buffer, this.Profit.fbeOffset + this.Profit.fbeSize)
    this.DayProfit = new fbe.FieldModelOptional(new FieldModelSorting(buffer, this.CurrentPrice.fbeOffset + this.CurrentPrice.fbeSize), buffer, this.CurrentPrice.fbeOffset + this.CurrentPrice.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelOrderStateSorting}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelOrderStateSorting}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.OrderId.fbeSize + this.MarginRate.fbeSize + this.Profit.fbeSize + this.CurrentPrice.fbeSize + this.DayProfit.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelOrderStateSorting}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.OrderId.fbeExtra + this.MarginRate.fbeExtra + this.Profit.fbeExtra + this.CurrentPrice.fbeExtra + this.DayProfit.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelOrderStateSorting}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelOrderStateSorting.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelOrderStateSorting}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 162
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelOrderStateSorting}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelOrderStateSorting}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.OrderId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.OrderId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.OrderId.fbeSize

    if ((fbeCurrentSize + this.MarginRate.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.MarginRate.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MarginRate.fbeSize

    if ((fbeCurrentSize + this.Profit.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Profit.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Profit.fbeSize

    if ((fbeCurrentSize + this.CurrentPrice.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.CurrentPrice.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CurrentPrice.fbeSize

    if ((fbeCurrentSize + this.DayProfit.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.DayProfit.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DayProfit.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelOrderStateSorting}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelOrderStateSorting}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelOrderStateSorting}
   * @param {!OrderStateSorting} fbeValue Default value, defaults is new OrderStateSorting()
   * @returns {!OrderStateSorting} OrderStateSorting value
   */
  get (fbeValue = new OrderStateSorting()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelOrderStateSorting}
   * @param {!OrderStateSorting} fbeValue OrderStateSorting value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.OrderId.fbeSize) <= fbeStructSize) {
      fbeValue.OrderId = this.OrderId.get()
    } else {
      fbeValue.OrderId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.OrderId.fbeSize

    if ((fbeCurrentSize + this.MarginRate.fbeSize) <= fbeStructSize) {
      fbeValue.MarginRate = this.MarginRate.get()
    } else {
      fbeValue.MarginRate = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MarginRate.fbeSize

    if ((fbeCurrentSize + this.Profit.fbeSize) <= fbeStructSize) {
      fbeValue.Profit = this.Profit.get()
    } else {
      fbeValue.Profit = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Profit.fbeSize

    if ((fbeCurrentSize + this.CurrentPrice.fbeSize) <= fbeStructSize) {
      fbeValue.CurrentPrice = this.CurrentPrice.get()
    } else {
      fbeValue.CurrentPrice = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CurrentPrice.fbeSize

    if ((fbeCurrentSize + this.DayProfit.fbeSize) <= fbeStructSize) {
      fbeValue.DayProfit = this.DayProfit.get()
    } else {
      fbeValue.DayProfit = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DayProfit.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelOrderStateSorting}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelOrderStateSorting}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelOrderStateSorting}
   * @param {!OrderStateSorting} fbeValue OrderStateSorting value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelOrderStateSorting}
   * @param {OrderStateSorting} fbeValue OrderStateSorting value
   */
  setFields (fbeValue) {
    this.OrderId.set(fbeValue.OrderId)
    this.MarginRate.set(fbeValue.MarginRate)
    this.Profit.set(fbeValue.Profit)
    this.CurrentPrice.set(fbeValue.CurrentPrice)
    this.DayProfit.set(fbeValue.DayProfit)
  }
}

export { FieldModelOrderStateSorting };

/**
 * Fast Binary Encoding OrderStateSorting model
 */
class OrderStateSortingModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelOrderStateSorting(this.buffer, 4)
  }

  /**
   * Get the OrderStateSorting model
   * @this {!OrderStateSortingModel}
   * @returns {!FieldModelOrderStateSorting} model OrderStateSorting model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!OrderStateSortingModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!OrderStateSortingModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return OrderStateSortingModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!OrderStateSortingModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelOrderStateSorting.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!OrderStateSortingModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!OrderStateSortingModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!OrderStateSortingModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!OrderStateSortingModel}
   * @param {!OrderStateSorting} value OrderStateSorting value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!OrderStateSortingModel}
   * @param {!OrderStateSorting} value OrderStateSorting value, defaults is new OrderStateSorting()
   * @return {!object} Deserialized OrderStateSorting value and its size
   */
  deserialize (value = new OrderStateSorting()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new OrderStateSorting(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new OrderStateSorting(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!OrderStateSortingModel}
   * @param {!number} prev Previous OrderStateSorting model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { OrderStateSortingModel };
